import React, {useRef, useState} from "react"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../../hooks/useAsyncResult"
import OrgRiskSettingsRulesEditMode from "./OrgRiskSettingsRulesEditMode"
import OrgRiskSettingsViewMode, {BankDetails} from "./OrgRiskSettingsViewMode"
import {useAccessToken, useIsUnitUser} from "../../../services/auth"
import {useModal} from "react-modal-hook"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {get, OrgBankAgreementBankRiskSettings} from "../../../resources/bankAgreementDefaults"
import {
	convertToBankAgreementDefaultsRiskSettings,
	convertToRiskSettings,
	RiskSettingsType,
} from "../../../components/RiskSettings/RiskSettingsUtils"
import ResetToDefaultRiskSettingsConfirmationModal from "../../../components/RiskSettings/Modals/ResetToDefaultRiskSettingsConfirmationModal"
import {updateOrgBankAgreementRiskSettings} from "../../../resources/orgBankAgreement"
import SaveRiskSettingsConfirmationModal from "../../../components/RiskSettings/Modals/SaveRiskSettingsConfirmationModal"
import CancelRiskSettingsConfirmationModal from "../../../components/RiskSettings/Modals/CancelRiskSettingsConfirmationModal"
import {FormikProps} from "formik"
import {getOrgName, Org} from "../../../resources/org"
import {hasPermission} from "../../../services/permission"

interface OrgRiskSettingsProps {
	agreementId: string
	org: Org
	bankId: string
	bankRiskSettings: OrgBankAgreementBankRiskSettings
	bankName?: string
	refresh: () => void
	refreshToken: number
}

export default function OrgRiskSettings({
	agreementId,
	org,
	bankId,
	bankRiskSettings,
	bankName,
	refresh,
	refreshToken,
}: OrgRiskSettingsProps) {
	const accessToken = useAccessToken()
	const bankAgreementDefaults = useAsyncResult(() => get(accessToken, bankId), [refreshToken])
	const isUnitUser = useIsUnitUser()
	const bankDetails: BankDetails =
		isUnitUser && bankName ? {bankName, showBankDetails: true} : {bankName: undefined, showBankDetails: false}
	return (
		<AsyncResultComponent asyncResult={bankAgreementDefaults} pendingComponent={<></>}>
			{({value: bankAgreementDefaults}) => {
				const riskSettings = convertToRiskSettings(bankRiskSettings)
				const defaultRiskSettings = convertToRiskSettings(bankAgreementDefaults.attributes.riskSettings)
				const formRef = useRef<FormikProps<RiskSettingsType>>()
				const [editMode, setEditMode] = useState<boolean>(false)
				const setEditModeToFalse = () => setEditMode(false)
				const [showResetToDefaultRiskSettingsConfirmationModal, hideResetToDefaultRiskSettingsConfirmationModal] =
					useModal(() => {
						const [state, callback] = useAsyncResultIdle(updateOrgBankAgreementRiskSettings)
						const {
							allowedIndividualIdentificationTypes,
							allowedSolePropsIdentificationTypes,
							allowedBusinessIdentificationTypes,
						} = bankAgreementDefaults.attributes.riskSettings
						const bankRiskSettingsToPatch = {
							allowedIndividualIdentificationTypes,
							allowedSolePropsIdentificationTypes,
							allowedBusinessIdentificationTypes,
						}
						const onSubmit = async () => {
							await callback(accessToken, agreementId, bankRiskSettingsToPatch, org.id, bankId)
						}
						return (
							<ResetToDefaultRiskSettingsConfirmationModal
								state={state}
								onSubmit={onSubmit}
								close={hideResetToDefaultRiskSettingsConfirmationModal}
								refresh={refresh}
								orgName={getOrgName(org)}
							/>
						)
					}, [])
				const [showCancelConfirmationModal, hideCancelConfirmationModal] = useModal(
					() => (
						<CancelRiskSettingsConfirmationModal close={hideCancelConfirmationModal} onSubmit={setEditModeToFalse} />
					),
					[]
				)
				const [showSaveConfirmationModal, hideSaveConfirmationModal] = useModal(() => {
					const [state, callback] = useAsyncResultIdle(updateOrgBankAgreementRiskSettings)
					const onSubmit = async () => {
						if (formRef.current) {
							const formValues = formRef.current.values
							const patch = convertToBankAgreementDefaultsRiskSettings(formValues)
							await callback(accessToken, agreementId, patch, org.id, bankId)
						}
					}
					return (
						<SaveRiskSettingsConfirmationModal
							state={state}
							onSubmit={onSubmit}
							close={hideSaveConfirmationModal}
							refresh={refresh}
						/>
					)
				}, [])
				return editMode ? (
					<OrgRiskSettingsRulesEditMode
						initialValues={riskSettings}
						defaultValue={defaultRiskSettings}
						onCancel={showCancelConfirmationModal}
						onSave={showSaveConfirmationModal}
						formRef={formRef}
					/>
				) : (
					<OrgRiskSettingsViewMode
						values={riskSettings}
						defaultValues={defaultRiskSettings}
						onResetToDefault={showResetToDefaultRiskSettingsConfirmationModal}
						setEditMode={setEditMode}
						canEdit={hasPermission("orgBankAgreement", "update")}
						bankId={bankId}
						bankDetails={bankDetails}
					/>
				)
			}}
		</AsyncResultComponent>
	)
}
